export type ReportFilterKind = 'text' | 'number' | 'date';
export type ReportFilterOperator = 'contains' | 'equals' | 'gte' | 'lte' | 'between';

export interface ReportFilterDescriptor {
  operator: ReportFilterOperator;
  value?: string;
  valueTo?: string;
}

/**
 * Chuẩn hóa text dùng chung cho các filter báo cáo.
 *
 * Dữ liệu hiển thị có thể có dấu, khác hoa/thường hoặc khoảng trắng thừa; filter
 * phải so sánh trên cùng một dạng để "nguyen", "Nguyễn" và " nguyen " cho cùng
 * kết quả. `đ` không được tách bởi NFD nên phải map riêng.
 */
export function normalizeReportText(value: unknown): string {
  return String(value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[đĐ]/g, 'd')
    .toLowerCase()
    .trim()
    .replace(/\s+/g, ' ');
}

/** Parse số nhập từ filter, hỗ trợ cả số thập phân và phân cách hàng nghìn. */
export function parseReportNumber(value: unknown): number | undefined {
  if (typeof value === 'number') return Number.isFinite(value) ? value : undefined;
  const raw = String(value ?? '').trim().replace(/\s/g, '');
  if (!raw) return undefined;

  const sign = raw.startsWith('-') ? '-' : '';
  const unsigned = raw.replace(/^[+-]/, '');
  const hasComma = unsigned.includes(',');
  const hasDot = unsigned.includes('.');
  let normalized = unsigned;

  if (hasComma && hasDot) {
    // Dấu cuối cùng là dấu thập phân; các dấu còn lại là phân cách nghìn.
    const decimalSeparator = unsigned.lastIndexOf(',') > unsigned.lastIndexOf('.') ? ',' : '.';
    const groupingSeparator = decimalSeparator === ',' ? '.' : ',';
    normalized = unsigned.split(groupingSeparator).join('').replace(decimalSeparator, '.');
  } else if (hasComma || hasDot) {
    const separator = hasComma ? ',' : '.';
    const parts = unsigned.split(separator);
    // 1.234 hoặc 1.234.567 được hiểu là số nguyên có phân cách nghìn; 1.5 là
    // số thập phân. Đây là quy tắc phù hợp với cách nhập số của các báo cáo VN.
    if (parts.length > 2 || (parts.length === 2 && parts[1].length === 3 && parts[0].length <= 3)) {
      normalized = parts.join('');
    } else {
      normalized = unsigned.replace(separator, '.');
    }
  }

  const parsed = Number(`${sign}${normalized}`);
  return Number.isFinite(parsed) ? parsed : undefined;
}

export function parseReportFilter(value: unknown): ReportFilterDescriptor {
  if (value && typeof value === 'object') {
    const candidate = value as Partial<ReportFilterDescriptor>;
    return {
      operator: candidate.operator || 'contains',
      value: candidate.value,
      valueTo: candidate.valueTo,
    };
  }
  try {
    const parsed = JSON.parse(String(value ?? '')) as Partial<ReportFilterDescriptor>;
    return {
      operator: parsed.operator || 'contains',
      value: parsed.value,
      valueTo: parsed.valueTo,
    };
  } catch {
    return { operator: 'contains', value: String(value ?? '') };
  }
}

/** Áp dụng đúng toán tử theo kiểu dữ liệu của cột. */
export function matchesReportFilter(raw: unknown, filterValue: unknown, kind: ReportFilterKind): boolean {
  const filter = parseReportFilter(filterValue);
  if (!filter.value && !filter.valueTo) return true;

  if (kind === 'number') {
    const actual = parseReportNumber(raw);
    const from = parseReportNumber(filter.value);
    const to = parseReportNumber(filter.valueTo);
    if (actual === undefined || from === undefined) return false;
    if (filter.operator === 'equals') return actual === from;
    if (filter.operator === 'gte') return actual >= from;
    if (filter.operator === 'lte') return actual <= from;
    return to !== undefined && actual >= from && actual <= to;
  }

  if (kind === 'date') {
    const actual = String(raw ?? '').slice(0, 10);
    if (!actual || !filter.value) return false;
    if (filter.operator === 'equals') return actual === filter.value;
    return Boolean(filter.valueTo) && actual >= filter.value && actual <= filter.valueTo!;
  }

  const actual = normalizeReportText(raw);
  const expected = normalizeReportText(filter.value);
  return filter.operator === 'equals' ? actual === expected : actual.includes(expected);
}

/**
 * Ghép mọi đoạn text con của một option antd về một chuỗi phẳng.
 *
 * `children` của `<Select.Option>` có thể là chuỗi, số, hoặc mảng do JSX nội suy
 * (`{code} - {name}` cho ra `['CH01', ' - ', 'Cửa hàng']`); so khớp trên từng
 * đoạn rời sẽ trượt cụm nằm vắt qua hai đoạn.
 */
function optionText(node: unknown): string {
  if (node === null || node === undefined || typeof node === 'boolean') return '';
  if (Array.isArray(node)) return node.map(optionText).join('');
  if (typeof node === 'object') {
    const props = (node as { props?: { children?: unknown } }).props;
    return props ? optionText(props.children) : '';
  }
  return String(node);
}

/**
 * `filterOption` dùng chung cho Select lọc phía client trong các báo cáo.
 *
 * Filter mặc định của antd so khớp nguyên văn nên gõ "cua" không ra "Cửa hàng".
 * Chuẩn hóa CẢ HAI vế bằng `normalizeReportText` để bỏ dấu, bỏ phân biệt
 * hoa/thường và khoảng trắng thừa.
 */
export function reportSelectFilterOption(
  input: string,
  option?: { label?: unknown; children?: unknown },
): boolean {
  const haystack = normalizeReportText(optionText(option?.label ?? option?.children));
  return haystack.includes(normalizeReportText(input));
}
